import React, { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Rnd } from 'react-rnd';
import { useEventListener } from 'ahooks';
import { Loader, Tooltip } from '@mantine/core';
import { translate } from '@shakkerai/util-i18n';
import { Icon } from '@shakkerai/components';
import { NumberInputGroup, notifySuccess } from '@shakkerai/pro-components';
import { MAX_GENERATOR_SIZE } from '@shakkerai/pro-constants';
import { BASETYPE_VALUE_TO_LABEL, SDXL_MODELS_FAMILY } from '@shakkerai/pro-models';
import { findBestSizeByReferenceSize } from '@shakkerai/pro-utils';
import { updateUnitData } from '@starflow/app/helpers/flow';
import { updatePromptFromImage, applyControlNetFromImage, fixHiresOptions } from '../../../helpers';
import { useLoadings } from '@shakkerai/pro-store';
import { useTempState, setTempState } from '@starflow/app/store';
import { DropImage } from './dropimage';
import '../reference.scss';

const modeOptions = [
  {
    strength: 0.25,
    mode: 'creative',
    label: translate('imgref_similarity_creative'),
  },
  {
    strength: 0.5,
    mode: 'similar',
    label: translate('imgref_similarity_original'),
  },
  {
    strength: 0.25,
    mode: 'custom',
    label: translate('imgref_similarity_cus'),
  },
];

const ImageInputOptions = React.memo(({ generator, onReUploadImage, uploading, externalKey }) => {
  const containerRef = useRef(null);
  const [position, setPosition] = useState(null);
  const { imageReference, prompt, baseType } = generator.data;

  const isSimple = useTempState('globalMode') === 'simple';

  const promptMatching = useLoadings('fetch-prompt-' + imageReference?.id);
  const isNotSDXL = !SDXL_MODELS_FAMILY[baseType];

  const handleCloseWindow = useCallback(() => {
    setTempState({ exclusiveWindow: null });
  }, []);

  const handleChangeStrength = useCallback(
    (strength) => {
      updateUnitData(generator.id, { imageReference: { ...imageReference, strength } });
    },
    [generator.id, imageReference],
  );

  const handleChangeMode = useCallback(
    ({ mode, strength, label }) => {
      updateUnitData(generator.id, { imageReference: { ...imageReference, mode, strength, label } });
    },
    [generator.id, imageReference],
  );

  const handleGlobalClick = useCallback((event) => {
    if (containerRef.current?.contains(event.target)) {
      return;
    }

    if (event.target?.closest('[data-role="btn-reload-cn-image"]')) {
      return;
    }

    if (event.target?.closest('[data-trigger-window="image-input-options"]')) {
      return;
    }

    if (event.target?.closest('.mantine-Popover-dropdown')) {
      return;
    }

    setTempState({ exclusiveWindow: null });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleMatchPrompt = useCallback(
    async (e) => {
      e && e.stopPropagation();
      if (promptMatching) return;
      if (!imageReference) return;

      updatePromptFromImage(imageReference, generator.id);
      // 修改图转词的原图
      if (imageReference?.url) {
        setTempState({
          imagePromptElement: {
            url: imageReference.url,
            id: imageReference.id || 'image-input-info',
          },
        });
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [imageReference, promptMatching, prompt, generator],
  );

  const handleApplyPose = useCallback(() => {
    if (!imageReference) return;
    applyControlNetFromImage(imageReference, generator.id, 'openpose');
  }, [generator, imageReference]);

  const handleApplyFacial = useCallback(() => {
    if (!imageReference) return;
    applyControlNetFromImage(imageReference, generator.id, 'face');
  }, [generator, imageReference]);

  const handleApplyReferenceSize = useCallback(() => {
    if (!imageReference) return;

    const fixedSize = findBestSizeByReferenceSize(imageReference?.width, imageReference?.height, MAX_GENERATOR_SIZE);

    if (fixedSize.width !== imageReference?.width || fixedSize.height !== imageReference?.height) {
      notifySuccess(translate('generator_resize_toast'));
    }

    updateUnitData(generator.id, fixedSize);
    fixHiresOptions(generator, 'generateSize');
  }, [generator, imageReference]);

  useEventListener('click', handleGlobalClick);

  useEffect(() => {
    const entryElementDOM = document.getElementById(`prompt-input-${generator.id}`);

    if (!entryElementDOM) return;

    const { x, y } = entryElementDOM.getBoundingClientRect();
    let position = { x: x, y: y - 515 };

    setPosition(position);
  }, [generator.id]);

  useEffect(() => {
    if (externalKey) {
      handleMatchPrompt();
      handleApplyReferenceSize();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [externalKey]);

  if (!position) {
    return null;
  }

  const strengthValue = imageReference?.strength === undefined ? 0.25 : imageReference?.strength;

  return createPortal(
    <Rnd
      scale={1}
      default={position}
      resizeHandleWrapperClass="hidden"
      dragHandleClassName="sub-drag-handle"
      className={`z-[101] image-reference-popup-wrap`}
    >
      <div ref={containerRef} className="z-20 w-[300px] bg-white shadow-light rounded-sm">
        <div className="sub-drag-handle flex items-center justify-between px-4 h-11 cursor-move">
          <span className="text-[#2d2d2d] font-bold font-grotesk text-sm">{translate('prompt_imgtoimg')}</span>
          <button onClick={handleCloseWindow}>
            <Icon fontSize={16} className="icon-border_close-1 text-[#2d2d2d]" />
          </button>
        </div>
        <div className="max-h-[70vh] overflow-auto tiny-scrollbar">
          <div className="flex flex-col items-stretch px-4 pt-4 border-t border-[#efefef]">
            <span className="text-[#2d2d2d] font-grotesk text-xs">{translate('cn_weight')}</span>
            <div className="mt-1 flex gap-2">
              {modeOptions.map((item) => {
                return (
                  <button
                    key={item.mode}
                    disabled={imageReference?.mode === item.mode}
                    onClick={() => handleChangeMode(item)}
                    className="flex-1 h-[26px] cursor-pointer bg-[#F4F6F8] disabled:bg-primary/10 hover:bg-[#f1f2f3] border border-transparent disabled:border-primary rounded-[2px] text-center text-[#2D2D2D] text-xs"
                  >
                    {item.label}
                  </button>
                );
              })}
            </div>
            {imageReference?.mode === 'custom' && (
              <div className="w-[150px] flex items-center mt-3">
                <NumberInputGroup
                  size="xs"
                  step={0.05}
                  min={0}
                  max={1}
                  inputWidth={42}
                  value={strengthValue}
                  onChange={handleChangeStrength}
                />
              </div>
            )}
          </div>
          <div className="mt-3 px-4">
            <h5 className="flex-1 text-[#2d2d2d] font-grotesk text-xs">{translate('img2img_use_as_reference')}</h5>
            <div className="mt-1 flex">
              <button
                onClick={handleApplyPose}
                className="px-3 h-[26px] mr-2 cursor-pointer rounded-[2px] text-center text-[#2D2D2D] text-xs bg-[#f4f6f8] hover:bg-[#f1f2f3]"
              >
                <Icon className="icon-prozhankai" fontSize={10} color="#2d2d2d" />
                <span className="ml-1 translate-y-px">{translate('imgref_mode_openpose')}</span>
              </button>
              {!isSimple && (
                <Tooltip
                  disabled={!isNotSDXL}
                  label={translate(`cn_basetype_unsupported_tip`, { type: BASETYPE_VALUE_TO_LABEL[baseType] })}
                >
                  <button
                    disabled={isNotSDXL}
                    onClick={handleApplyFacial}
                    className="px-3 h-[26px] cursor-pointer rounded-[2px] text-center text-[#2D2D2D] text-xs bg-[#f4f6f8] hover:bg-[#f1f2f3] disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <Icon className="icon-prozhankai" fontSize={10} color="#2d2d2d" />
                    <span className="ml-1 translate-y-px">{translate('cn_face')}</span>
                  </button>
                </Tooltip>
              )}
            </div>
          </div>
          <div className="relative mt-4 bg-[#F7F7F7]">
            <DropImage
              generatorId={generator.id}
              imageInfo={imageReference}
              onReUploadImage={onReUploadImage}
              uploading={uploading}
            />
          </div>
          <div className="flex items-center h-[50px]">
            <button
              onClick={handleMatchPrompt}
              className="flex items-center flex-nowrap justify-center flex-1 h-[50px] disabled:opacity-30 disabled:pointer-events-none"
            >
              {promptMatching ? (
                <Loader size={14} color="#5c636b" />
              ) : (
                <Icon className="shk-danchuang-zhuanhuantishici" fontSize={14} color="inherit" />
              )}
              <span className="ml-2 whitespace-nowrap text-xs text-[#2d2d2d]">{translate('img_prompt')}</span>
            </button>
            <div className="h-4 w-px bg-[#dfdfdf]" />
            <button
              onClick={handleApplyReferenceSize}
              className="flex items-center flex-nowrap  justify-center flex-1 h-[50px] disabled:opacity-30 disabled:pointer-events-none"
            >
              <Icon className="shk-tongbuchicun" fontSize={14} color="inherit" />
              <span className="ml-2 whitespace-nowrap text-xs text-[#2d2d2d]">{translate('img_size')}</span>
            </button>
          </div>
        </div>
      </div>
    </Rnd>,
    document.body,
  );
});
export default ImageInputOptions;
